import React, { useRef, useEffect, useImperativeHandle, forwardRef, useMemo, useCallback, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

/**
 * 富文本編輯器組件，支持流程變量插入
 *
 * @param {Object} props
 * @param {string} props.value - 編輯器的值（HTML 格式）
 * @param {Function} props.onChange - 值變更回調函數
 * @param {string} props.placeholder - 佔位符文本
 * @param {number} props.height - 編輯器高度（像素），默認 300
 * @param {Object} props.toolbarTooltips - 可選，為內建工具列按鈕設定滑鼠懸停說明（原生 title）
 */
const RichTextEditor = forwardRef(({
  value = '',
  onChange,
  placeholder = '',
  height = 300,
  customToolbarButtons = [],
  toolbarTooltips = {},
}, ref) => {
  const quillRef = useRef(null);
  const wrapperRef = useRef(null);
  const isInternalChangeRef = useRef(false);
  const isFocusedRef = useRef(false);
  const lastValueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const customToolbarButtonsRef = useRef(customToolbarButtons);

  const [showHtml, setShowHtml] = useState(false);
  const [htmlCode, setHtmlCode] = useState('');

  onChangeRef.current = onChange;
  customToolbarButtonsRef.current = customToolbarButtons;

  const handleToggleHtml = useCallback(() => {
    if (!showHtml) {
      setHtmlCode(lastValueRef.current || '');
      setShowHtml(true);
    } else {
      // Exit HTML mode: push edited HTML to parent; sync effect will inject into Quill
      onChangeRef.current?.(htmlCode);
      setShowHtml(false);
    }
  }, [showHtml, htmlCode]);

  const insertTextAtCursor = useCallback((textToInsert) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    isInternalChangeRef.current = true;
    const range = quill.getSelection(true);
    if (range) {
      quill.insertText(range.index, textToInsert, 'user');
      quill.setSelection(range.index + textToInsert.length);
    } else {
      const length = quill.getLength();
      quill.insertText(length - 1, textToInsert, 'user');
      quill.setSelection(length - 1 + textToInsert.length);
    }
    const newContent = quill.root.innerHTML;
    lastValueRef.current = newContent;
    onChangeRef.current?.(newContent);
    isInternalChangeRef.current = false;
  }, []);

  const insertTextAtCursorRef = useRef(insertTextAtCursor);
  insertTextAtCursorRef.current = insertTextAtCursor;

  // 當 value 從外部變更時（例如載入設定），同步到 Quill；編輯中或與 lastValueRef 相同則跳過
  useEffect(() => {
    const nextVal = value ?? '';
    const doSync = () => {
      if (isFocusedRef.current) return;
      const quill = quillRef.current?.getEditor?.();
      if (!quill || typeof quill.clipboard?.dangerouslyPasteHTML !== 'function') return;
      if (nextVal === lastValueRef.current) return;
      const currentHtml = quill.root?.innerHTML ?? '';
      if (nextVal !== currentHtml) {
        lastValueRef.current = nextVal;
        isInternalChangeRef.current = true;
        quill.clipboard.dangerouslyPasteHTML(nextVal || '<p><br></p>', 'silent');
        isInternalChangeRef.current = false;
      }
    };
    const t = window.setTimeout(doSync, 0);
    return () => clearTimeout(t);
  }, [value]);

  useImperativeHandle(ref, () => ({
    insertVariable: (variableName) => {
      const variableText = `\${${variableName}}`;
      insertTextAtCursorRef.current(variableText);
    },
    getEditor: () => quillRef.current?.getEditor(),
  }));

  const modules = useMemo(() => {
    const toolbar = [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link'],
    ];

    const initialButtons = customToolbarButtonsRef.current || [];
    if (initialButtons.length > 0) {
      toolbar.push(initialButtons.map((btn) => btn.key));
    }
    toolbar.push(['clean']);

    const handlers = {};
    initialButtons.forEach((btn) => {
      handlers[btn.key] = () => {
        const current = (customToolbarButtonsRef.current || []).find((b) => b.key === btn.key);
        insertTextAtCursorRef.current(current?.insertText || btn.insertText || '');
      };
    });

    return {
      toolbar: {
        container: toolbar,
        handlers,
      },
      clipboard: {
        matchVisual: false,
      },
    };
  }, []);

  useEffect(() => {
    if (showHtml) return undefined;
    const timer = window.setTimeout(() => {
      const root = wrapperRef.current;
      if (!root) return;
      const toolbar = root.querySelector('.ql-toolbar');
      if (!toolbar) return;

      const tt = toolbarTooltips || {};
      const pairs = [
        ['.ql-bold', tt.bold],
        ['.ql-italic', tt.italic],
        ['.ql-underline', tt.underline],
        ['.ql-strike', tt.strike],
        ['.ql-picker.ql-header', tt.header],
        ['.ql-picker.ql-color', tt.color],
        ['.ql-picker.ql-background', tt.background],
        ['.ql-list[value="ordered"]', tt.listOrdered],
        ['.ql-list[value="bullet"]', tt.listBullet],
        ['.ql-picker.ql-align', tt.align],
        ['.ql-link', tt.link],
        ['.ql-clean', tt.clean],
      ];
      pairs.forEach(([selector, title]) => {
        if (!title) return;
        const el = toolbar.querySelector(selector);
        if (el) el.setAttribute('title', title);
      });

      (customToolbarButtonsRef.current || []).forEach((btn) => {
        const buttonEl = toolbar.querySelector(`.ql-${btn.key}`);
        if (buttonEl) {
          const displayText = btn.icon || btn.label || btn.key;
          buttonEl.textContent = displayText;
          buttonEl.style.width = 'auto';
          buttonEl.style.padding = '0 8px';
          buttonEl.style.whiteSpace = 'nowrap';
          buttonEl.style.lineHeight = '24px';
          if (btn.title) {
            buttonEl.setAttribute('title', btn.title);
          }
        }
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [toolbarTooltips, customToolbarButtons, showHtml]);

  useEffect(() => {
    if (showHtml) return undefined;
    const root = wrapperRef.current;
    if (!root) return undefined;
    const editorEl = root.querySelector('.ql-editor');
    if (!editorEl) return undefined;

    const onFocus = () => { isFocusedRef.current = true; };
    const onBlur = () => { isFocusedRef.current = false; };
    editorEl.addEventListener('focus', onFocus);
    editorEl.addEventListener('blur', onBlur);
    return () => {
      editorEl.removeEventListener('focus', onFocus);
      editorEl.removeEventListener('blur', onBlur);
    };
  }, [showHtml]);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'align',
    'link',
  ];

  const handleChange = (content, delta, source) => {
    if (source !== 'user') return;
    if (!isInternalChangeRef.current) {
      lastValueRef.current = content ?? '';
      onChangeRef.current?.(content);
    }
  };

  const safeValue = value || '';

  return (
    <div ref={wrapperRef} style={{ position: 'relative', height: `${height}px` }}>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleToggleHtml}
        title={showHtml ? 'Switch to Visual Editor' : 'Edit HTML source'}
        style={{
          position: 'absolute',
          top: 5,
          right: 6,
          zIndex: 10,
          background: showHtml ? '#1677ff' : 'transparent',
          color: showHtml ? '#fff' : '#555',
          border: `1px solid ${showHtml ? '#1677ff' : '#ccc'}`,
          borderRadius: 3,
          padding: '0 6px',
          height: 22,
          fontSize: 12,
          cursor: 'pointer',
          lineHeight: '20px',
          fontFamily: 'monospace',
          fontWeight: 'bold',
        }}
      >
        {'</>'}
      </button>

      {showHtml ? (
        <textarea
          value={htmlCode}
          onChange={(e) => setHtmlCode(e.target.value)}
          style={{
            width: '100%',
            height: '100%',
            padding: '8px 44px 8px 8px',
            fontFamily: 'monospace',
            fontSize: 13,
            lineHeight: 1.6,
            border: '1px solid #d9d9d9',
            borderRadius: 4,
            resize: 'none',
            boxSizing: 'border-box',
            outline: 'none',
            color: '#1a1a2e',
            background: '#f8f9ff',
          }}
        />
      ) : (
        <ReactQuill
          ref={quillRef}
          value={safeValue}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          style={{ height: `${height - 42}px` }}
          theme="snow"
        />
      )}
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
